import React, { PureComponent, Fragment } from 'react'
import { array, bool, func, number, shape, string, any, object } from 'prop-types'
import track from 'react-tracking'
import ReactMarkdown from 'markdown-to-jsx'

import { Button } from '@ynap/button'
import BEMHelper from '@ynap/bem-helper'
import EmptyList from '@ynap/empty-list'
import Notification from '@ynap/notification'

import OrdersSection from './OrdersSection'
import { myaccountPageData } from '../../analytics'

const bem = new BEMHelper('OrderHistory')

@track({})
export default class OrderHistory extends PureComponent {
    static propTypes = {
        orders: shape({
            recent: shape({
                items: array,
                pagination: shape({
                    totalOrders: number,
                    shouldDisplayLoadMore: bool,
                    currentPage: number,
                    totalOrdersDisplayed: number,
                }),
            }),
            past: shape({
                items: array,
                pagination: shape({
                    totalOrders: number,
                    shouldDisplayLoadMore: bool,
                    currentPage: number,
                    totalOrdersDisplayed: number,
                }),
            }),
        }),
        isFetchingOrderHistory: shape({
            recent: bool,
            past: bool,
        }).isRequired,
        pageSize: number,
        noOrdersLink: string,
        fetchOrderHistory: func.isRequired,
        className: string,
        messages: shape({
            orderHistory: shape({
                title: func.isRequired,
                description: func.isRequired,
                noOrdersTitle: func.isRequired,
                noOrdersDescription: func.isRequired,
                paginationDetails: func.isRequired,
                loadMore: func.isRequired,
                noOrdersLinkTitle: func.isRequired,
                errorLoadingMoreOrders: func.isRequired,
                pageError: func.isRequired,
                retryButton: func.isRequired,
            }),
        }),
        skeletonOrders: number,
        fetchOrderHistoryError: shape({
            recent: bool,
            past: bool,
        }),
        fetchPastOrderHistoryError: any,
        config: any,
        clearStateOnMount: bool,
        locale: object.isRequired,
    }

    static defaultProps = {
        skeletonOrders: 6,
    }

    @track(
        myaccountPageData({
            name: 'my orders',
            type: 'my orders page',
            subCategory1: 'my orders',
        })
    )
    componentDidMount() {}

    renderHeading() {
        const { messages, config, orderDelayMessaging, locale } = this.props
        const { fullPageHeader, disableExchangesIn } = config?.head?.flags || {}
        const returnWindowConfig = config?.head?.returnWindow

        const showDelayMessaging = orderDelayMessaging?.enabled && orderDelayMessaging.locales.includes(locale.id)
        const returnWindow = returnWindowConfig?.countries?.includes?.(locale.country)
            ? returnWindowConfig?.specialWindow
            : returnWindowConfig?.default

        const delayMessage =
            orderDelayMessaging?.message?.[locale.language] ||
            messages.orderDetails.delayMessage?.({ country: locale.country })

        const DelayComponent = (props) => <ReactMarkdown {...props}>{delayMessage}</ReactMarkdown>
        const description =
            disableExchangesIn?.includes(locale.country) && messages.orderHistory?.refundsOnlyDescription
                ? messages.orderHistory?.refundsOnlyDescription()
                : messages.orderHistory.description()

        return (
            <div className={bem('heading')}>
                {!fullPageHeader && <div className={bem('title')}>{messages.orderHistory.title()}</div>}
                <div className={bem('description')}>{description}</div>
                {messages.orderHistory.terms && (
                    <div className={bem('description')}>
                        {messages.orderHistory.terms({
                            returnWindow,
                        })}
                    </div>
                )}
                {showDelayMessaging && (
                    <Notification className={bem('orderDelay')} component={DelayComponent} type="info" />
                )}
            </div>
        )
    }

    render() {
        const {
            locale,
            orders: { recent, past },
            messages,
            noOrdersLink,
            className,
            isFetchingOrderHistory,
            fetchOrderHistoryError,
            fetchPastOrderHistoryError,
            fetchOrderHistory,
            skeletonOrders,
            pageSize,
            config: { head, showExchangeDerivedOrderIds } = {},
            nativeApp,
        } = this.props

        const commonProps = {
            fetchOrderHistory,
            fetchOrderHistoryError,
            showExchangeDerivedOrderIds,
            isFetchingOrderHistory,
            skeletonOrders,
            messages,
            pageSize,
        }

        const shouldRenderErrorState = (!recent && !past) || (fetchOrderHistoryError && fetchPastOrderHistoryError)
        const shouldRenderEmptyState = recent?.items?.length === 0 && past?.items?.length === 0
        const disableExchanges = head?.flags?.disableExchangesIn?.includes(locale.country)
        const recentOrdersDescription =
            disableExchanges && messages.orderHistory?.canBeRefundedNotExchanged
                ? messages.orderHistory.canBeRefundedNotExchanged()
                : messages.orderHistory.canBeRefunded()

        return (
            <div className={bem(null, null, className)}>
                {shouldRenderErrorState ? (
                    <EmptyList
                        className={bem('empty', nativeApp ? 'nativeApp' : '')}
                        variant={'myAccountError'}
                        title={messages.orderHistory.pageError()}
                    >
                        <Button
                            type="primary-link"
                            className={bem('emptyLink')}
                            onClick={() => {
                                fetchOrderHistory(1, pageSize, null, 'recent')
                                fetchOrderHistory(1, pageSize, null, 'past')
                            }}
                        >
                            {messages.orderHistory.retryButton()}
                        </Button>
                    </EmptyList>
                ) : shouldRenderEmptyState ? (
                    <EmptyList
                        className={bem('empty', nativeApp ? 'nativeApp' : '')}
                        icon={<div className={bem('noOrdersIcon')} />}
                        title={messages.orderHistory.noOrdersTitle()}
                        details={messages.orderHistory.noOrdersDescription()}
                    >
                        <Button component="a" href={noOrdersLink} type="primary-link" className={bem('emptyLink')}>
                            {messages.orderHistory.noOrdersLinkTitle()}
                        </Button>
                    </EmptyList>
                ) : (
                    <Fragment>
                        {this.renderHeading()}
                        <OrdersSection
                            {...commonProps}
                            category="recent"
                            bemClassName="recentOrdersToggle"
                            orders={recent}
                            description={recentOrdersDescription}
                            startsOpen={typeof recent.recordSetTotal == 'undefined' || recent.recordSetTotal > 0}
                        />
                        <OrdersSection
                            {...commonProps}
                            category="past"
                            bemClassName="pastOrdersToggle"
                            orders={past}
                            description={messages.orderHistory.cannotBeRefunded()}
                        />
                    </Fragment>
                )}
            </div>
        )
    }
}
